import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Subscription({ mode }) {
  const loc = useLocation();
  const nav = useNavigate();
  const params = new URLSearchParams(loc.search);
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(mode === "cancel" ? "cancelled" : (sessionId ? "polling" : "idle"));
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatus("paid");
        toast.success("Subscription activated!");
        return;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      setAttempts(a => a + 1);
    } catch (e) {
      setAttempts(a => a + 1);
    }
  }, [sessionId]);

  useEffect(() => {
    if (mode === "cancel" || !sessionId) return;
    if (status === "paid" || status === "expired") return;
    if (attempts >= 8) { setStatus("timeout"); return; }
    const t = setTimeout(poll, attempts === 0 ? 800 : 2000);
    return () => clearTimeout(t);
  }, [attempts, sessionId, status, mode, poll]);

  return (
    <div data-testid="subscription-page" className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16">
      <div className="bg-white rounded-3xl p-10 border border-slate-100 shadow-[0_20px_60px_rgba(2,132,199,0.12)] max-w-md w-full text-center">
        {(status === "polling" || status === "idle") && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-sky-500 animate-spin mb-4" strokeWidth={2.5}/>
            <h1 className="text-3xl font-black text-slate-900">Confirming payment…</h1>
            <p className="text-slate-500 mt-2">Hang tight, we're checking with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="text-emerald-600" strokeWidth={2.5}/>
            </div>
            <h1 className="text-3xl font-black text-slate-900">You're in!</h1>
            <p className="text-slate-600 mt-2">Welcome to The Haddis Academy. Your subscription is active.</p>
            <Button data-testid="sub-go-dashboard" onClick={()=>nav("/dashboard")}
                    className="mt-6 rounded-full bg-sky-400 hover:bg-sky-500 text-white font-bold h-12 px-8 shadow-lg shadow-sky-200">
              Go to dashboard
            </Button>
          </>
        )}
        {status === "cancelled" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
              <XCircle className="text-amber-600" strokeWidth={2.5}/>
            </div>
            <h1 className="text-3xl font-black text-slate-900">Payment cancelled</h1>
            <p className="text-slate-600 mt-2">No worries — your trial is still active. You can subscribe anytime.</p>
            <Button data-testid="sub-back-dashboard" onClick={()=>nav("/dashboard")}
                    className="mt-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold h-12 px-8">
              Back to dashboard
            </Button>
          </>
        )}
        {status === "timeout" && (
          <>
            <Sparkles className="w-12 h-12 mx-auto text-amber-500 mb-4" strokeWidth={2.5}/>
            <h1 className="text-3xl font-black text-slate-900">Still processing</h1>
            <p className="text-slate-600 mt-2">Stripe is taking a bit longer than usual. We'll email you when it's confirmed.</p>
            <Link to="/dashboard"><Button className="mt-6 rounded-full">Back to dashboard</Button></Link>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-rose-500 mb-4" strokeWidth={2.5}/>
            <h1 className="text-3xl font-black text-slate-900">Session expired</h1>
            <p className="text-slate-600 mt-2">Please try subscribing again.</p>
            <Link to="/dashboard"><Button className="mt-6 rounded-full">Back to dashboard</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
